import { useEffect, useMemo, useState } from "react"

import { Spin } from "antd"
import { Line } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import SharedKpiTabs from "../../../../Components/KpiTabs/KpiTabs"
import { useIncognito } from "../../../../hooks/useIncognito"
import { buildKpiComparisonOverlay } from "../../../DashboardPage/components/chartsOptions"
import { tooltipLine } from "../../../DashboardPage/components/chartsPlagins"
import { DEFLECT_SECTION } from "../../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../../redux/deflectAnalytics.slice"
import { buildKpiDataset, COMPARISON_LINE_STYLE, kpiTrendOptions } from "../../utils/chartOptions"
import { buildKpiTabs } from "../../utils/kpiTabs"
import LineChartSkeleton from "../Skeleton/LineChartSkeleton"

const CHART_HEIGHT = 480

// Deflect Analytics headline row: the shared KPI tabs over a single trend line. The tabs read
// kpis/, the chart reads trend/ for the selected tab's metric, so switching tabs asks for a new
// line and leaves the figures where they are.
const KpiTabs = ({ getData, comparing }) => {
  const { t } = useTranslation()
  const theme = useSelector((state) => state.nav.theme)
  // The axis labels are drawn once per render; remounting redraws them masked or unmasked.
  const incognito = useIncognito()
  const kpis = useSelector(selectDeflectSection(DEFLECT_SECTION.KPIS))
  const kpisFirstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.KPIS))
  const trend = useSelector(selectDeflectSection(DEFLECT_SECTION.TREND))
  const trendFetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.TREND))
  const trendFirstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.TREND))
  const [selectedKey, setSelectedKey] = useState("lookups")

  useEffect(() => {
    getData(DEFLECT_SECTION.KPIS)
  }, [getData])

  const tabs = useMemo(() => buildKpiTabs(kpis, t), [kpis, t])
  const selected = tabs.find((tab) => tab.key === selectedKey)
  const metric = selectedKey

  // An account with no lookups at all has no trend to draw: the tabs stay at zero, the chart goes
  // (RCA-2-04). A window with none on an account that has lookups still draws its flat line.
  const noLookupsEver = kpis?.account_lookups_count === 0

  useEffect(() => {
    if (noLookupsEver) return

    getData(DEFLECT_SECTION.TREND, { metric })
  }, [getData, metric, noLookupsEver])

  const chartOptions = useMemo(
    () =>
      kpiTrendOptions({
        t,
        theme,
        metric,
        title: selected?.label,
        prefix: selected?.prefix,
        suffix: selected?.suffix,
        decimals: selected?.decimals,
        totalLabel: selected?.totalLabel,
        totals: trend?.data,
        breakdown: trend?.breakdown,
        comparisonTotals: trend?.comparison?.data,
        comparisonBreakdown: trend?.comparison?.breakdown
      }),
    [
      t,
      theme,
      metric,
      selected?.label,
      selected?.prefix,
      selected?.suffix,
      selected?.decimals,
      selected?.totalLabel,
      trend?.data,
      trend?.breakdown,
      trend?.comparison?.data,
      trend?.comparison?.breakdown
    ]
  )

  const chartData = useMemo(() => {
    const labels = trend?.labels ?? []
    return {
      labels,
      datasets: [
        buildKpiDataset({
          label: selected?.label ?? "",
          data: trend?.data ?? [],
          pointCount: labels.length
        }),
        ...buildKpiComparisonOverlay({
          labels,
          series: trend?.comparison?.data,
          comparisonLabels: trend?.comparison?.labels,
          label: trend?.comparison_period?.label || t("Previous period")
        }).map((overlay) => ({ ...overlay, ...COMPARISON_LINE_STYLE }))
      ]
    }
  }, [trend, selected?.label, t])

  return (
    <SharedKpiTabs
      tabs={tabs}
      selectedKey={selected?.key}
      onSelect={setSelectedKey}
      loading={kpisFirstLoad}
      withComparison={comparing}
    >
      {/* Until kpis/ first answers it is not known whether the chart is drawn at all, so it stays a
          skeleton rather than flashing a line it may take back. */}
      {!noLookupsEver &&
        (kpisFirstLoad || trendFirstLoad ? (
          <LineChartSkeleton height={CHART_HEIGHT} />
        ) : (
          <Spin spinning={trendFetching}>
            <Line
              key={String(incognito)}
              options={chartOptions}
              data={chartData}
              height={CHART_HEIGHT}
              plugins={[tooltipLine]}
            />
          </Spin>
        ))}
    </SharedKpiTabs>
  )
}

export default KpiTabs
